/**
 * PayPal Service (frontend)
 *
 * Thin wrapper around the Cloud Functions in functions/index.js, which are the
 * only place that ever talks to PayPal's API or sees the PayPal Secret.
 * The frontend never computes the charge amount — the Cloud Function looks up
 * the price server-side so it can't be tampered with via dev tools.
 */

import { httpsCallable } from "firebase/functions"
import { functions } from "../config/firebase"

// ── Suites & Villas ──────────────────────────────────────────────────────

/**
 * @param {Object} params
 * @param {string} params.villaId - Firestore villa doc id
 * @param {string} params.checkIn - YYYY-MM-DD
 * @param {string} params.checkOut - YYYY-MM-DD
 * @returns {Promise<{orderId: string, total: string, nights: number}>}
 */
export const createSuitePayPalOrder = async ({ villaId, checkIn, checkOut }) => {
  const call = httpsCallable(functions, "createSuitePayPalOrder")
  const result = await call({ villaId, checkIn, checkOut })
  return result.data
}

/**
 * @param {string} orderId - PayPal order ID returned by createSuitePayPalOrder
 * @param {Object} booking - Booking form details to attach to the saved booking
 * @returns {Promise<{success: boolean, amountPaid: number, pointsAwarded: number}>}
 */
export const captureSuitePayPalOrder = async (orderId, booking) => {
  const call = httpsCallable(functions, "captureSuitePayPalOrder")
  const result = await call({ orderId, booking })
  return result.data
}

// ── Wellness Center ──────────────────────────────────────────────────────

/**
 * @param {Object} params
 * @param {string} params.serviceId - Firestore wellnessServices doc id
 * @returns {Promise<{orderId: string, total: string}>}
 */
export const createWellnessPayPalOrder = async ({ serviceId }) => {
  const call = httpsCallable(functions, "createWellnessPayPalOrder")
  const result = await call({ serviceId })
  return result.data
}

/**
 * @param {string} orderId - PayPal order ID returned by createWellnessPayPalOrder
 * @param {Object} booking - Booking form details to attach to the saved booking
 * @returns {Promise<{success: boolean, amountPaid: number, pointsAwarded: number}>}
 */
export const captureWellnessPayPalOrder = async (orderId, booking) => {
  const call = httpsCallable(functions, "captureWellnessPayPalOrder")
  const result = await call({ orderId, booking })
  return result.data
}

// ── PB Pavilion ───────────────────────────────────────────────────────────

/**
 * @param {Object} params
 * @param {string} params.eventId - Firestore pavilionEvents doc id
 * @param {number} params.guests - Number of guests
 * @returns {Promise<{orderId: string, total: string, guests: number}>}
 */
export const createPavilionPayPalOrder = async ({ eventId, guests }) => {
  const call = httpsCallable(functions, "createPavilionPayPalOrder")
  const result = await call({ eventId, guests })
  return result.data
}

/**
 * @param {string} orderId - PayPal order ID returned by createPavilionPayPalOrder
 * @param {Object} booking - Booking form details to attach to the saved booking
 * @returns {Promise<{success: boolean, amountPaid: number, pointsAwarded: number}>}
 */
export const capturePavilionPayPalOrder = async (orderId, booking) => {
  const call = httpsCallable(functions, "capturePavilionPayPalOrder")
  const result = await call({ orderId, booking })
  return result.data
}

// ── Cart Checkout ─────────────────────────────────────────────────────────────

/**
 * @param {{ items: Array<{category, itemId, checkIn?, checkOut?, nights?, guests?}> }} params
 * @returns {Promise<{orderId: string, total: string}>}
 */
export const createCartPayPalOrder = async ({ items }) => {
  const call = httpsCallable(functions, "createCartPayPalOrder")
  const result = await call({ items })
  return result.data
}

/**
 * @param {{ orderId: string, items: CartItem[] }} params
 * @returns {Promise<{success: boolean, pointsAwarded: number, totalCharged: number, bookingIds: string[]}>}
 */
export const captureCartPayPalOrder = async ({ orderId, items }) => {
  const call = httpsCallable(functions, "captureCartPayPalOrder")
  const result = await call({ orderId, items })
  return result.data
}

const paypalService = {
  createSuitePayPalOrder,
  captureSuitePayPalOrder,
  createWellnessPayPalOrder,
  captureWellnessPayPalOrder,
  createPavilionPayPalOrder,
  capturePavilionPayPalOrder,
  createCartPayPalOrder,
  captureCartPayPalOrder,
}

export default paypalService
